import React, {
  useEffect, useMemo, useState, useRef,
} from 'lib/react';
import {
  EVGrid,
} from 'lib/components';
import { useDispatch, useSelector } from 'lib/redux';
import { useTranslation } from 'lib/translation';
import history from 'store/CreateHistory';
import { cancelOrderSelection } from 'features/order/Order.actions';
import ValidationMessageDialog from 'commonComponents/validationMessageDialog';
import { HISTORY_ACTIONS } from 'constants/AppConstants';
import { ORDER_ROUTE } from 'constants/RouteConstants';
import RetryScreen from 'commonComponents/retry-screen';
import PageNotFound from 'commonComponents/pageNotFound';
import { isSubscriptionDashboardV2EnabledSelector } from 'utils/appSelectors.utils';
import { ESX_FOR_UPGRADE } from 'constants/FeatureFlag.constants';
import FinalizeOrderStyles from './FinalizeOrderStyles';
import IncludeSpecialInstructions from './components/IncludeSpecialInstructions.component';
import PaymentSection from './components/paymentSection/PaymentSection.component';
import { displayIncludeSpecialInstructions } from './FinalizeOrder.utils';
import { isPropertyDataSelected as checkIsPropertyDataSelected } from '../order/Order.utils';
import OrderSummary from './components/orderSummary';
import OrderSummarySubscription from './components/orderSummary/OrderSummarySubscription.component';
import { loadFinalizeOrderPage, loadUpgradeReportFinalizeOrder, resetFinalizeOrderPage } from './FinalizeOrder.actions';
import OrderFailedRetryPage from './components/OrderFailedRetryPage.component';
import FinalizeOrderLoader from './FinalizeOrderLoader.component';
import EmailRecipients from './components/emailRecipients';
import PropertyDetails from './components/property-details';
import ClaimInformation from './components/claimInformation';
import MeasurementInstructions from './components/MeasurementInstructions.component';
import SubstitutionsAndCustomization from './components/substitutionsAndCustomization/SubstitutionsAndCustomization.component';

function FinalizeOrder() {
  const paymentSectionRef = useRef(null);
  const {
    selectedPrimaryProduct, selectedWallsProduct, selectedOtherProducts,
    primarySubscriptionProductsWithUsage,
    controlStates: { paymentInformation, sendReportsTo }, loadingPage, placeOrderError, loadFinalizeOrderError,
    mapPoints, upgradedReportNotFound, upgradedReportError, bluePrintFileId, myevRefreshFinalizeSubstitutions,
    esxForUpgradeFlag,
  } = useSelector(({
    order, finalizeOrder, orderAddresses, orderHistory, userData: { controlStates }, placeOrder, featureFlag,
  }) => ({
    ...order,
    reportDetails: orderHistory.reportDetails,
    controlStates,
    loadingPage: finalizeOrder.loadingPage,
    placeOrderError: finalizeOrder.placeOrderError,
    loadFinalizeOrderError: finalizeOrder.loadFinalizeOrderError,
    mapPoints: orderAddresses.mapPoints,
    upgradedReportNotFound: finalizeOrder.upgradedReportNotFound,
    upgradedReportError: finalizeOrder.upgradedReportError,
    bluePrintFileId: placeOrder.bluePrintFileId,
    myevRefreshFinalizeSubstitutions: featureFlag.myevRefreshFinalizeSubstitutions,
    esxForUpgradeFlag: featureFlag[ESX_FOR_UPGRADE],
  }));
  const isSubscriptionDashboardV2Enabled = useSelector(isSubscriptionDashboardV2EnabledSelector);
  const isOrderHistoryEsxFilesUpgradeEnabled = Boolean(esxForUpgradeFlag && !isSubscriptionDashboardV2Enabled);
  const classes = FinalizeOrderStyles();
  const { t } = useTranslation();
  const hasSelectedPrimarySubscriptionSummary = useMemo(() => {
    const selectedPrimaryId = Number(selectedPrimaryProduct?.ProductID);
    if (!selectedPrimaryId || !Array.isArray(primarySubscriptionProductsWithUsage)) return false;
    return primarySubscriptionProductsWithUsage.some((p) => (
      Number(p?.productId) === selectedPrimaryId && Boolean(p?.isSubscription)
    ));
  }, [selectedPrimaryProduct?.ProductID, primarySubscriptionProductsWithUsage]);

  const dispatch = useDispatch();
  const [cancelDialog, setCancelDialog] = useState('CLOSED_VALIDATION');
  const [isCancelConfirm, setIsCancelConfirm] = useState(() => { });

  const searchParams = new URLSearchParams(window.location.search);
  const upgradeReportId = searchParams.get('upgradeReportId');
  const upgradeESX = Boolean(
    isOrderHistoryEsxFilesUpgradeEnabled
    && searchParams.get('upgradeESX') === 'true',
  );
  // Check if Property Data product is selected
  const isPropertyDataSelected = checkIsPropertyDataSelected(selectedPrimaryProduct);
  // #genai Keep cancel-confirm as a bound handler so history.block is not nested past Sonar S2004.
  const confirmCancelForPath = (pathname) => () => {
    dispatch(cancelOrderSelection());
    history.unblock();
    history.push(pathname);
  };

  const handleHistoryBlock = (tx, action) => {
    if (tx.pathname === history.location.pathname) return true;
    if (action === HISTORY_ACTIONS.POP) {
      dispatch(resetFinalizeOrderPage());
      history.unblock();
      history.push(tx.pathname);
    } else {
      setCancelDialog('CANCEL_ORDER_VALIDATION');
      setIsCancelConfirm(() => confirmCancelForPath(tx.pathname));
    }
    return false;
  };

  // Block transitions from finalize order page to show confirmation dialog for canceling current order
  const cancelConfirmationDialog = () => {
    history.unblock = history.block(handleHistoryBlock);
  };

  useEffect(() => {
    if (upgradeReportId) {
      dispatch(loadUpgradeReportFinalizeOrder(upgradeReportId, upgradeESX));
      cancelConfirmationDialog();
    } else if (
      (selectedPrimaryProduct || selectedWallsProduct
        || Object.keys(selectedOtherProducts || {}).length > 0)
        && ((mapPoints?.length > 0) || Boolean(bluePrintFileId))
    ) {
      dispatch(loadFinalizeOrderPage());
      cancelConfirmationDialog();
    } else {
      // Redirect to order page if it is not upgrade report page or there are no selected products or no mapPoints
      history.push(ORDER_ROUTE);
    }

    return () => {
      if (history.unblock) {
        history.unblock();
        delete history.unblock;
      }
    };
  }, []);

  const handleValidationDialogClose = () => {
    setCancelDialog('CLOSED_VALIDATION');
  };

  const validationDialogProps = {
    CLOSED_VALIDATION: {
      open: false,
      title: '',
      message: '',
    },
    CANCEL_ORDER_VALIDATION: {
      open: true,
      title: t('cancel_order_dialog_title'),
      message: t('cancel_order_dialog_description'),
      cancelButtonText: t('stay_on_page'),
      okayButtonText: t('cancel_order'),
    },
  };

  if (loadingPage) return <FinalizeOrderLoader />;

  if (placeOrderError) {
    return (
      <>
        <OrderFailedRetryPage upgradeReportId={upgradeReportId} />
        <ValidationMessageDialog
          {...validationDialogProps[cancelDialog]}
          handleClose={handleValidationDialogClose}
          handleNext={isCancelConfirm}
          isNext
          cancelButtonClass={classes.cancelButton}
          okayButtonClass={classes.okayButton}
        />
      </>
    );
  }

  if (upgradedReportNotFound) return <PageNotFound />;

  return (
    <>
      <ValidationMessageDialog
        {...validationDialogProps[cancelDialog]}
        handleClose={handleValidationDialogClose}
        handleNext={isCancelConfirm}
        isNext
        cancelButtonClass={classes.cancelButton}
        okayButtonClass={classes.okayButton}
      />
      {
        (loadFinalizeOrderError || upgradedReportError)
          ? (
            <RetryScreen handleRetryButtonClick={() => (upgradeReportId
              ? dispatch(loadUpgradeReportFinalizeOrder(upgradeReportId, upgradeESX))
              : dispatch(loadFinalizeOrderPage()))}
            />
          )
          : (
            <EVGrid container className={classes.finalizeOrder}>
              <EVGrid item xs={12} sm={12} md={8} lg={8} className={classes.finalizeOrderPanels}>
                <PropertyDetails upgradeReportId={upgradeReportId} />
                {upgradeReportId && <MeasurementInstructions />}
                {/* #genai - optional chaining resolves to undefined for a missing product, same as before */}
                {displayIncludeSpecialInstructions(selectedPrimaryProduct?.ProductID)
                  && !isPropertyDataSelected && <IncludeSpecialInstructions />}
                {!bluePrintFileId && <ClaimInformation />}
                {!sendReportsTo?.hidden && <EmailRecipients />}
                {myevRefreshFinalizeSubstitutions && !isPropertyDataSelected && <SubstitutionsAndCustomization />}
                {!paymentInformation?.hidden && <PaymentSection ref={paymentSectionRef} />}
              </EVGrid>
              {(isSubscriptionDashboardV2Enabled || hasSelectedPrimarySubscriptionSummary) ? (
                <OrderSummarySubscription paymentSectionRef={paymentSectionRef} />
              ) : (
                <OrderSummary paymentSectionRef={paymentSectionRef} />
              )}
            </EVGrid>
          )
      }
    </>
  );
}

export default FinalizeOrder;
